/* global Promise */
import {Button, DatePicker, Form, Input, Select} from 'antd';
import moment from 'moment';
import {getUserTimezone} from 'utils/helper';
import {
  WALLET_AMOUNT_OPERATORS,
  WALLET_CURRENCIES,
  WALLET_FIELDS,
  WALLET_MOVEMENTS,
  WALLET_OPERATIONS,
  WALLET_PAYMENT_METHODS,
  WALLET_PAYMENT_PROVIDERS,
  WALLET_STATUSES,
  buildWalletAudience,
  describeWalletAudience,
  isWalletAmount,
} from '../utils/walletAudience';
import styles from './walletActivityFilter.module.scss';

const {RangePicker} = DatePicker;

const useWalletValues = () => {
  const form = Form.useFormInstance();
  const values = {};
  WALLET_FIELDS.forEach(field => {
    values[field] = Form.useWatch(field, form);
  });
  return values;
};

/** The section's collapse header: its title and how many filters are on. */
export const WalletActivityHeader = ({className}) => {
  const count = Object.keys(buildWalletAudience(useWalletValues())).length;
  return (
    <div className={styles.header}>
      <h1 className={className}>Filter by Wallet Activity</h1>
      {count > 0 && (
        <span className={styles.count}>
          {count} filter{count === 1 ? '' : 's'} set
        </span>
      )}
    </div>
  );
};

// Each filter takes several values; a user qualifies with any one of them.
const Choice = ({name, label, options}) => (
  <Form.Item name={name} label={label} className={styles.field}>
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder="Any"
      maxTagCount="responsive"
      options={options}
    />
  </Form.Item>
);

const Group = ({title, hint, children}) => (
  <section className={styles.group}>
    <h3>{title}</h3>
    {hint && <p className={styles.hint}>{hint}</p>}
    <div className={styles.grid}>{children}</div>
  </section>
);

/**
 * Wallet activity audience filters. Everything chosen must match the same
 * wallet ledger entry, so "Withdrawal + PayPal + more than 100" means one
 * PayPal withdrawal over 100, not three separate entries.
 */
export const WalletActivityFilter = () => {
  const form = Form.useFormInstance();
  const values = useWalletValues();
  const summary = describeWalletAudience(values);
  const operator = values.walletAmountOperator;

  return (
    <div className={styles.root}>
      <div className={summary ? styles.summaryOn : styles.summary}>
        <span>
          {summary ||
            'No wallet filter yet. Choose any of the filters below; the ones you leave empty are ignored.'}
        </span>
        {summary && (
          <Button
            size="small"
            type="link"
            onClick={() =>
              form.setFieldsValue(
                Object.fromEntries(WALLET_FIELDS.map(f => [f, undefined])),
              )
            }>
            Clear wallet filters
          </Button>
        )}
      </div>

      <Group title="Transaction">
        <Choice
          name="walletCurrency"
          label="Currency"
          options={WALLET_CURRENCIES}
        />
        <Choice
          name="walletOperation"
          label="Operation"
          options={WALLET_OPERATIONS}
        />
        <Choice name="walletStatus" label="Status" options={WALLET_STATUSES} />
      </Group>

      <Group title="How the money moved">
        <Choice
          name="walletPaymentMethod"
          label="Payment method"
          options={WALLET_PAYMENT_METHODS}
        />
        <Choice
          name="walletPaymentProvider"
          label="Payment provider"
          options={WALLET_PAYMENT_PROVIDERS}
        />
        <Choice
          name="walletMovement"
          label="Wallet movement"
          options={WALLET_MOVEMENTS}
        />
      </Group>

      <Group
        title="Amount and date"
        hint="Amounts are before fees, in the entry's own currency. With no currency chosen, every currency is compared at face value.">
        <div className={styles.amount}>
          <Form.Item
            name="walletAmountOperator"
            label="Amount"
            dependencies={['walletAmountValue']}
            rules={[
              ({getFieldValue}) => ({
                validator(_, value) {
                  return !value && getFieldValue('walletAmountValue')
                    ? Promise.reject(new Error('Pick a comparison'))
                    : Promise.resolve();
                },
              }),
            ]}
            className={styles.operator}>
            <Select
              allowClear
              placeholder="Any amount"
              options={WALLET_AMOUNT_OPERATORS}
            />
          </Form.Item>
          <Form.Item
            name="walletAmountValue"
            label=" "
            dependencies={['walletAmountOperator']}
            rules={[
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (!String(value ?? '').trim()) {
                    return getFieldValue('walletAmountOperator')
                      ? Promise.reject(new Error('Enter an amount'))
                      : Promise.resolve();
                  }
                  return isWalletAmount(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Use a number like 100 or 100.25'),
                      );
                },
              }),
            ]}
            className={styles.value}>
            <Input
              aria-label="Amount value"
              inputMode="decimal"
              placeholder={operator ? 'e.g. 100.25' : 'Pick a comparison first'}
              disabled={!operator && !values.walletAmountValue}
            />
          </Form.Item>
        </div>
        <Form.Item
          name="walletDates"
          label="Transaction date"
          className={styles.dates}>
          <RangePicker
            allowEmpty={[true, true]}
            showTime={{
              defaultValue: [
                moment('00:00:00', 'HH:mm:ss'),
                moment('23:59:59', 'HH:mm:ss'),
              ],
            }}
            format="YYYY-MM-DD HH:mm"
            placeholder={['From any date', 'To any date']}
            renderExtraFooter={() => (
              <span className={styles.hint}>
                Your timezone is: {getUserTimezone()}
              </span>
            )}
          />
        </Form.Item>
      </Group>
    </div>
  );
};
